import { ReactElement, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useFormController } from '@dreryk/react-common';
import { AddCircleIcon, Link, LinkSize, StyledFlex } from '@dreryk/react-components';

import { magistralDrugTranslationKeys } from '../../../translations';
import { OnMedicineSelection } from '../PrescriptionIssuingPanel.types';
import { MagistralDrugCreateModalOpener } from '../magistralDrug/create/MagistralDrugCreateModal.opener';
import { OnMagistralDrugCreated } from '../magistralDrug/create/MagistralDrugCreateForm.types';

import { useMedicineSelectorIcdCodesState } from './medicineSelector.hooks';
import { MedicineSelectorFromBean } from './medicineSelector.types';
import { MedicineSelectorActionInput } from './MedicineSelectorActionInput/MedicineSelectorActionInput';
import { MedicineSelectorIcd10CodeChips } from './MedicineSelectorActionInput/MedicineSelectorIcd10CodeChips';
import { PharmindexLogo } from './PharmindexLogo/PharmindexLogo';
import { SearchResultsMedicineSelectorTable } from './MedicineSelectorTable/SearchResultsMedicineSelectorTable/SearchResultsMedicineSelectorTable';
import { StyledMedicineSelector } from './StyledMedicineSelector';

const MEDICINE_SELECT_MIN_SEARCH_CHARACTERS = 3;

type MedicineSearchProps = {
   readonly patientId: Patient['id'];
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly onMedicineSelection: OnMedicineSelection;
   readonly onMagistralDrugCreated: OnMagistralDrugCreated;
   readonly children: ReactElement;
};

export const MedicineSearch = (props: MedicineSearchProps): JSX.Element => {
   const { register, watch, reset } = useFormController<MedicineSelectorFromBean>({
      medicineSearchText: null,
   });

   const location = useLocation();
   const previousPathnameRef = useRef(location.pathname);

   useEffect(() => {
      if (previousPathnameRef.current !== location.pathname) {
         previousPathnameRef.current = location.pathname;
         reset({ medicineSearchText: null });
      }
   }, [location.pathname, reset]);

   const { selectCode, deselectCode, icd10Codes, selectedIcd10Codes } = useMedicineSelectorIcdCodesState(
      props.visitDiagnoses
   );

   const medicineSearchText = watch('medicineSearchText');
   const searchAvailable =
      (medicineSearchText && medicineSearchText.length >= MEDICINE_SELECT_MIN_SEARCH_CHARACTERS) ||
      Boolean(selectedIcd10Codes.length);
   const medicineSelectorTableSearchText =
      medicineSearchText && medicineSearchText?.length >= MEDICINE_SELECT_MIN_SEARCH_CHARACTERS
         ? medicineSearchText
         : null;

   return (
      <StyledMedicineSelector>
         <MedicineSelectorActionInput
            register={register}
            labelCustomContent={
               <>
                  <StyledFlex flexWrap={'wrap'} gap={'8px'} alignItems={'center'}>
                     <PharmindexLogo />
                     <MedicineSelectorIcd10CodeChips
                        icd10Codes={icd10Codes}
                        onIcd10CodeSelect={selectCode}
                        onIcd10CodeDeselect={deselectCode}
                     />
                  </StyledFlex>
                  <MagistralDrugCreateModalOpener onMagistralDrugCreated={props.onMagistralDrugCreated}>
                     {({ openModal }) => (
                        <Link
                           testId={'openMagistralDrugModal'}
                           label={magistralDrugTranslationKeys.tabName}
                           onClick={openModal}
                           size={LinkSize.NORMAL}
                           icon={<AddCircleIcon />}
                           nowrap
                        />
                     )}
                  </MagistralDrugCreateModalOpener>
               </>
            }
         />
         {searchAvailable ? (
            <SearchResultsMedicineSelectorTable
               medicineSearchText={medicineSelectorTableSearchText}
               onMedicineSelection={props.onMedicineSelection}
               patientId={props.patientId}
               selectedIcd10Codes={selectedIcd10Codes}
               visitDiagnoses={props.visitDiagnoses}
            />
         ) : (
            props.children
         )}
      </StyledMedicineSelector>
   );
};
